import React, { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid } from '@mui/x-data-grid';
import InputMask from 'react-input-mask';
import { useUser } from '../../Hooks/useUser';
import axios from "../../api/axios";

const References = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user: contextUser } = useUser();
  const user = props.User ?? contextUser;

  const [bomReference, setBomReference] = useState({
    BPReferencesID: 0,
    BP_UserGUID: user?.UserGUID,
    BP_R_Name: '',
    BP_R_Phone: '',
  });

  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorFields, setErrorFields] = useState({});

  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);
  const [editMode, setEditMode] = useState(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [referenceToDelete, setReferenceToDelete] = useState(null);
  const handleDeleteConfirmClose = () => {
    setDeleteConfirmOpen(false);
    setReferenceToDelete(null);
  };

  const resetReference = useCallback(() => {
    setBomReference({
      BPReferencesID: 0,
      BP_UserGUID: user?.UserGUID,
      BP_R_Name: '',
      BP_R_Phone: '',
    });
  }, [user?.UserGUID]);

  // Keep BP_UserGUID synced in case user resolves after mount (admin mode)
  useEffect(() => {
    setBomReference(prev => ({ ...prev, BP_UserGUID: user?.UserGUID }));
  }, [user?.UserGUID]);

  const handleAdd = () => {
    setEditMode(false);
    setErrorFields({});
    resetReference();
    setOpen(true);
  };

  const handleEdit = (obj, params) => {
    setEditMode(true);
    setErrorFields({});
    setOpen(true);
  };

  const handleDeleteClick = (ob, params) => {
    setReferenceToDelete(params.row);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!referenceToDelete) return;

    axios.delete(`/api/references/${referenceToDelete["BPReferencesID"]}`)
      .then(() => {
        enqueueSnackbar("Reference Deleted Successfully");
        getData();
        handleDeleteConfirmClose();
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar(err?.response?.data?.error || "Reference Delete Failed", { variant: 'error' });
        handleDeleteConfirmClose();
      });
  };

  function handleFieldChange(input) {
    const target = input.target;
    const value = target.value;
    const name = target.id;
    setBomReference(prev => ({ ...prev, [name]: value }));
    if (errorFields[name]) {
      setErrorFields({ ...errorFields, [name]: false });
    }
  }

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  };

  const columns = [
    { field: 'BP_R_Name', headerName: 'Reference Name', flex: 1, minWidth: 200 },
    { field: 'BP_R_Phone', headerName: 'Reference Phone', flex: 1, minWidth: 200 },
    {
      field: "Actions", flex: 1, minWidth: 200,
      renderCell: (cellValues) => (
        <div className="d-flex gap-2">
          <Button
            variant="contained"
            color="primary"
            onClick={(event) => {
              setBomReference(cellValues.row);
              handleEdit(event, cellValues);
            }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={(event) => handleDeleteClick(event, cellValues)}
          >
            Delete
          </Button>
        </div>
      )
    },
  ];

  const getData = useCallback(() => {
    if (!user?.UserGUID) return;

    setLoading(true);
    axios.get(`/api/references/${user.UserGUID}`)
      .then((response) => {
        setReferences(response.data);
      })
      .catch((error) => {
        console.error(error);
        enqueueSnackbar('Failed to load references', { variant: 'error' });
      })
      .finally(() => setLoading(false));
  }, [user?.UserGUID, enqueueSnackbar]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleSubmit = async e => {
    e.preventDefault();

    const errors = {};
    if (!bomReference.BP_R_Name?.trim()) {
      errors.BP_R_Name = true;
      enqueueSnackbar("Please enter reference name", { variant: 'error' });
    }
    if (!bomReference.BP_R_Phone?.trim()) {
      errors.BP_R_Phone = true;
      enqueueSnackbar("Please enter reference phone number", { variant: 'error' });
    }

    if (Object.keys(errors).length > 0) {
      setErrorFields(errors);
      return;
    }
    setErrorFields({});
    setSaving(true);

    const request = editMode
      ? axios.put('/api/references', bomReference)
      : axios.post('/api/references', bomReference);

    try {
      await request;
      enqueueSnackbar(editMode ? "Reference Updated" : "Reference Saved");
      getData();
      handleClose();
      resetReference();
    } catch (ex) {
      console.error(ex);
      enqueueSnackbar(
        ex?.response?.data?.error || (editMode ? "Reference Update Failed" : "Reference Save Failed"),
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div id="tab4">
      <div className="row px-5 pt-5">
        <div className="col-md-12">
          <DataGrid
            flexGrow
            autoHeight={true}
            autoPageSize={true}
            pageSize={10}
            columns={columns}
            rows={references}
            getRowId={(row) => row.BPReferencesID}
            disableMultipleSelection={true}
            disableSelectionOnClick={true}
            loading={loading}
          />
        </div>
        <div className="col-md-12"> <a data-toggle="collapse" onClick={handleAdd} role="button" aria-expanded="false" aria-controls="add-reference" className="button-default collapsed">Add Reference</a>
          <div className="collapse" id="add-ref">
            <Modal open={open} onClose={handleClose} aria-labelledby='title' aria-describedby=''>
              <Box sx={modalStyle}>
                <IconButton
                  aria-label="close"
                  onClick={handleClose}
                  sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
                >
                  <CloseIcon />
                </IconButton>
                <Typography id="title" variant='h5'>References</Typography>
                <form onSubmit={handleSubmit}>
                  <div className="row pt-3">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="required-caption">Reference Name<span className="required-field">*</span></label>
                        <input id="BP_R_Name" type="text" maxLength="200" className={`form-control ${errorFields.BP_R_Name ? 'error-field' : ''}`} value={bomReference.BP_R_Name} placeholder="Reference Name" onChange={handleFieldChange} />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="required-caption">Reference Phone Number<span className="required-field">*</span></label>
                        <InputMask
                          mask="(999) 999-9999"
                          value={bomReference.BP_R_Phone || ''}
                          onChange={handleFieldChange}
                        >
                          {(inputProps) => (
                            <input
                              {...inputProps}
                              id="BP_R_Phone"
                              type="text"
                              className={`form-control ${errorFields.BP_R_Phone ? 'error-field' : ''}`}
                              placeholder="(123) 456-7890"
                            />
                          )}
                        </InputMask>
                      </div>
                    </div>
                    <div className="col-md-6 text-white py-1 d-flex gap-2 mt-3">
                      <a data-toggle="collapse" role="button" aria-expanded="false" aria-controls="save-skills" onClick={handleSubmit} className="button-default button-green collapsed" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', minHeight: '38px', padding: '8px 20px', opacity: saving ? 0.7 : 1, pointerEvents: saving ? 'none' : 'auto' }}>
                        {saving && <CircularProgress size={14} style={{ color: 'white' }} />}
                        Save
                      </a>
                      <a data-toggle="collapse" onClick={handleClose} role="button" aria-expanded="false" aria-controls="cancel-skills" className="button-default button-gray collapsed">Cancel</a>
                    </div>
                    <div className="col-md-6 text-white py-1 d-flex gap-2 justify-content-end mt-3">
                      {editMode && <a data-toggle="collapse" role="button" aria-expanded="false" aria-controls="delete-skills" className="button-default button-red collapsed">Delete</a>}
                    </div>
                  </div>
                </form>
              </Box>
            </Modal>
          </div>
        </div>

        <Modal open={deleteConfirmOpen} onClose={handleDeleteConfirmClose} aria-labelledby='delete-title' aria-describedby='delete-description'>
          <Box sx={modalStyle}>
            <IconButton
              aria-label="close"
              onClick={handleDeleteConfirmClose}
              sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
            <Typography id="delete-title" variant='h5' sx={{ mb: 2 }}>Confirm Delete</Typography>
            <Typography id="delete-description" variant='body1' sx={{ mb: 4 }}>
              Are you sure you want to delete this entry? This action cannot be undone.
            </Typography>
            <div className="row">
              <div className="col-md-12 text-white py-1 d-flex gap-2">
                <a role="button" onClick={handleDeleteConfirm} className="button-default button-red collapsed">Delete</a>
                <a role="button" onClick={handleDeleteConfirmClose} className="button-default button-gray collapsed">Cancel</a>
              </div>
            </div>
          </Box>
        </Modal>
      </div>
    </div>
  );
};
export default References;